import { Effect, pipe } from 'effect';

// Constants
import { END_POINTS } from '@/constants';
import {
  MESSAGES,
  STATUS_CODE,
  DEFAULT_PROJECTS_LIMIT,
} from '@shared/constants';

// Services
import { httpClient } from '@/services/http';

// Types
import { RequestConfig, ProjectListResponse } from '@/types';
import {
  Project,
  ProjectActivitiesResponse,
  ProjectMember,
} from '@shared/types';

/**
 * Fetches project information by project identity (ID or code)
 * @param projectIdentity - Project ID or project code
 * @returns Promise<Project['projectInfo']> - Project information
 */
export const getProjectInfo = (
  projectIdentity: string,
  requestConfig: RequestConfig,
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: () =>
          httpClient<Project['projectInfo']>(
            END_POINTS.GET_PROJECT_INFO(projectIdentity),
            requestConfig,
          ),
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

/**
 * Fetches project information by project identity (ID or code)
 * @param projectIdentity - Project ID or project code
 * @returns Promise<Project['projectInfo']> - Project information
 */
export const getProjectSimpleInfo = (
  identity: string,
  requestConfig: RequestConfig,
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: () =>
          httpClient<Project['projectInfo']>(
            END_POINTS.GET_PROJECT_SIMPLE_INFO(identity),
            requestConfig,
          ),
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

/**
 * Fetches all projects from the API
 * @returns Promise<Project[]> - Array of projects
 */
export const getAllProjects = async (options?: {
  requestConfig?: RequestConfig;
}) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const allProjects: Project['projectInfo'][] = [];
          let nextCursor: string | undefined;

          do {
            const response = await httpClient<ProjectListResponse>(
              END_POINTS.GET_ALL_PROJECTS(DEFAULT_PROJECTS_LIMIT, nextCursor),
              { ...options?.requestConfig, isFullResponse: true },
            );

            if (!response?.data?.data) {
              break;
            }

            // Add all users from current page
            allProjects.push(...response.data.data);

            // Get next cursor for pagination
            const { pagination } = response.data.meta || {};

            if (
              response.data?.data &&
              response.data.data.length < DEFAULT_PROJECTS_LIMIT
            ) {
              nextCursor = undefined;
            } else {
              nextCursor = pagination?.cursor;
            }
          } while (nextCursor);

          return {
            data: allProjects,
            error: null,
            status: STATUS_CODE.OK,
          };
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

/**
 * Fetches project members by project ID
 * @param projectId - Project ID
 * @returns Promise<ProjectMember[]> - Array of project members
 */
export const getProjectMember = ({
  projectId,
  requestConfig,
}: {
  projectId: string;
  requestConfig?: RequestConfig;
}) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: () =>
          httpClient<ProjectMember[]>(
            END_POINTS.GET_PROJECT_MEMBER(projectId),
            requestConfig,
          ),
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

/**
 * Fetches project activities by project ID
 * @param projectId - Project ID
 * @param cursor - Cursor for pagination
 * @returns Promise<ProjectActivitiesResponse> - Project activities response
 */
export const getProjectActivities = ({
  projectId,
  requestConfig,
  cursor = '',
}: {
  projectId: string;
  requestConfig?: RequestConfig;
  cursor?: string;
}) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: () =>
          httpClient<ProjectActivitiesResponse>(
            END_POINTS.GET_PROJECT_ACTIVITIES(projectId, cursor),
            { ...requestConfig, isFullResponse: true },
          ),
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );
